import { useState, useEffect } from 'react';
import { supabase } from '../../supabase';

function SocialCliente({ cliente, setPaginaAtiva, setClienteSocialAtivo }) {
  const [posts, setPosts] = useState([]);
  const [etapas, setEtapas] = useState([]);
  const [checklist, setChecklist] = useState({});
  const [config, setConfig] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [mesAtual, setMesAtual] = useState(new Date());

  useEffect(() => { if (cliente) carregarTudo(); }, [cliente, mesAtual]);

  const getMesRef = () => `${mesAtual.getFullYear()}-${String(mesAtual.getMonth() + 1).padStart(2, '0')}-01`;

  const carregarTudo = async () => {
    const mesRef = getMesRef();
    const inicioMes = mesRef;
    const fimMes = new Date(mesAtual.getFullYear(), mesAtual.getMonth() + 1, 0).toISOString().split('T')[0];

    const [{ data: ps }, { data: etps }, { data: checks }, { data: cfg }] = await Promise.all([
      supabase.from('social_posts').select('*').eq('cliente_id', cliente.id).gte('data_post', inicioMes).lte('data_post', fimMes).order('data_post'),
      supabase.from('social_etapas').select('*').eq('ativo', true).order('ordem'),
      supabase.from('social_checklist').select('*').eq('cliente_id', cliente.id).eq('mes_ref', mesRef),
      supabase.from('social_config_clientes').select('*').eq('cliente_id', cliente.id).single(),
    ]);
    setPosts(ps || []);
    setEtapas(etps || []);
    const map = {};
    (checks || []).forEach(c => { map[c.etapa_id] = c; });
    setChecklist(map);
    setConfig(cfg);
    setCarregando(false);
  };

  const toggleCheck = async (etapaId) => {
    const mesRef = getMesRef();
    const existente = checklist[etapaId];
    if (existente) {
      await supabase.from('social_checklist').update({
        concluido: !existente.concluido,
        concluido_em: !existente.concluido ? new Date().toISOString() : null,
      }).eq('id', existente.id);
    } else {
      await supabase.from('social_checklist').insert({
        cliente_id: cliente.id,
        etapa_id: etapaId,
        mes_ref: mesRef,
        concluido: true,
        concluido_em: new Date().toISOString(),
      });
    }
    carregarTudo();
  };

  const getMesLabel = () => {
    const meses = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];
    return `${meses[mesAtual.getMonth()]} ${mesAtual.getFullYear()}`;
  };

  const mudarMes = (dir) => {
    const novo = new Date(mesAtual);
    novo.setMonth(mesAtual.getMonth() + dir);
    setMesAtual(novo);
  };

  const postsPorStatus = {
    producao: posts.filter(p => p.status === 'producao').length,
    postado: posts.filter(p => p.status === 'postado').length,
    cancelado: posts.filter(p => p.status === 'cancelado').length,
  };

  const concluidos = Object.values(checklist).filter(c => c.concluido).length;
  const progressoPct = etapas.length > 0 ? (concluidos / etapas.length) * 100 : 0;

  const proximosPosts = posts.filter(p => {
    const hoje = new Date().toISOString().split('T')[0];
    return p.data_post >= hoje && p.status !== 'cancelado';
  }).slice(0, 5);

  const getStatusStyle = (status) => {
    if (status === 'postado') return { bg: '#0d2a0d', color: '#22c55e', label: 'Postado' };
    if (status === 'cancelado') return { bg: '#2a0d0d', color: '#ef4444', label: 'Cancelado' };
    return { bg: '#2a1f00', color: '#f59e0b', label: 'Produção' };
  };

  if (!cliente) return null;
  if (carregando) return <div className="flex items-center justify-center h-64"><p style={{ color: '#555' }}>Carregando...</p></div>;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => setPaginaAtiva('social')} className="text-sm" style={{ color: '#555' }}
            onMouseEnter={(e) => e.currentTarget.style.color = '#fff'}
            onMouseLeave={(e) => e.currentTarget.style.color = '#555'}>
            ← Social Media
          </button>
          <span style={{ color: '#333' }}>/</span>
          <h1 className="text-xl font-bold text-white">{cliente.nome}</h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1">
            <button onClick={() => mudarMes(-1)} className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: '#111', border: '1px solid #1a1a1a', color: '#aaa' }}>‹</button>
            <span className="text-sm font-medium text-white px-2">{getMesLabel()}</span>
            <button onClick={() => mudarMes(1)} className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: '#111', border: '1px solid #1a1a1a', color: '#aaa' }}>›</button>
          </div>
          <button
            onClick={() => { setClienteSocialAtivo(cliente); setPaginaAtiva('social_calendario'); }}
            className="text-sm px-4 py-2 rounded-lg font-medium"
            style={{ backgroundColor: '#DCF127', color: '#0a0a0a' }}>
            📅 Calendário
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-4 gap-3 mb-6">
        <div className="rounded-xl p-4" style={{ backgroundColor: '#111', border: '1px solid #1a1a1a' }}>
          <p className="text-xs mb-2" style={{ color: '#555' }}>Posts planejados</p>
          <p className="text-xl font-bold text-white">{posts.length}</p>
        </div>
        <div className="rounded-xl p-4" style={{ backgroundColor: '#111', border: '1px solid #1a1a1a' }}>
          <p className="text-xs mb-2" style={{ color: '#555' }}>Postados</p>
          <p className="text-xl font-bold" style={{ color: '#22c55e' }}>{postsPorStatus.postado}</p>
        </div>
        <div className="rounded-xl p-4" style={{ backgroundColor: '#111', border: '1px solid #1a1a1a' }}>
          <p className="text-xs mb-2" style={{ color: '#555' }}>Em produção</p>
          <p className="text-xl font-bold" style={{ color: '#f59e0b' }}>{postsPorStatus.producao}</p>
        </div>
        <div className="rounded-xl p-4" style={{ backgroundColor: '#111', border: '1px solid #1a1a1a' }}>
          <p className="text-xs mb-2" style={{ color: '#555' }}>Cancelados</p>
          <p className="text-xl font-bold" style={{ color: '#ef4444' }}>{postsPorStatus.cancelado}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* Checklist */}
        <div className="rounded-xl p-5" style={{ backgroundColor: '#111', border: '1px solid #1a1a1a' }}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-white">Checklist do mês</h2>
            <span className="text-xs" style={{ color: progressoPct === 100 ? '#22c55e' : '#555' }}>
              {concluidos}/{etapas.length} concluído
            </span>
          </div>
          <div className="w-full rounded-full mb-4" style={{ backgroundColor: '#1a1a1a', height: '4px' }}>
            <div className="rounded-full transition-all" style={{
              width: `${progressoPct}%`, height: '4px',
              backgroundColor: progressoPct === 100 ? '#22c55e' : '#DCF127'
            }} />
          </div>
          <div className="flex flex-col gap-2">
            {etapas.map(etapa => {
              const check = checklist[etapa.id];
              const concluido = check?.concluido;
              return (
                <div key={etapa.id}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg"
                  style={{ backgroundColor: '#1a1a1a' }}>
                  <button onClick={() => toggleCheck(etapa.id)}
                    className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0 transition-all"
                    style={{
                      backgroundColor: concluido ? '#DCF127' : 'transparent',
                      border: concluido ? 'none' : '1.5px solid #333',
                    }}>
                    {concluido && <span style={{ color: '#0a0a0a', fontSize: '11px', fontWeight: '700' }}>✓</span>}
                  </button>
                  <span className="text-sm flex-1" style={{ color: concluido ? '#555' : '#fff', textDecoration: concluido ? 'line-through' : 'none' }}>
                    {etapa.nome}
                  </span>
                  {concluido && check?.concluido_em && (
                    <span className="text-xs" style={{ color: '#333' }}>
                      {new Date(check.concluido_em).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Próximos posts */}
        <div className="rounded-xl p-5" style={{ backgroundColor: '#111', border: '1px solid #1a1a1a' }}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-white">Próximos posts</h2>
            <button
              onClick={() => { setClienteSocialAtivo(cliente); setPaginaAtiva('social_calendario'); }}
              className="text-xs transition-colors" style={{ color: '#555' }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#DCF127'}
              onMouseLeave={(e) => e.currentTarget.style.color = '#555'}>
              ver calendário →
            </button>
          </div>
          {proximosPosts.length === 0 ? (
            <div className="flex items-center justify-center h-32">
              <p className="text-sm" style={{ color: '#333' }}>Nenhum post agendado</p>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {proximosPosts.map(post => {
                const st = getStatusStyle(post.status);
                return (
                  <div key={post.id} className="flex items-center gap-3 px-3 py-2.5 rounded-lg"
                    style={{ backgroundColor: '#1a1a1a' }}>
                    <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: st.color }} />
                    <span className="text-sm text-white flex-1 truncate">{post.nome}</span>
                    <span className="text-xs flex-shrink-0" style={{ color: '#555' }}>
                      {new Date(post.data_post + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
                    </span>
                    <span className="text-xs px-2 py-0.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: st.bg, color: st.color }}>
                      {st.label}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Doc */}
          {config?.google_doc_url && (
            <div className="mt-4 pt-4" style={{ borderTop: '1px solid #1a1a1a' }}>
              <a href={config.google_doc_url} target="_blank" rel="noreferrer"
                className="text-xs flex items-center gap-2 transition-colors"
                style={{ color: '#555' }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#DCF127'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#555'}>
                📄 Abrir Google Doc →
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SocialCliente;